import type { CSSProperties } from 'react';

import type { Speaker } from '@/papermill/transcript';

import styles from './SpeakerBadge.module.css';

export interface SpeakerBadgeProps {
  speaker: Pick<Speaker, 'name' | 'role' | 'slot'>;
  size?: 'sm' | 'md';
  /** Show the role beside the name, e.g. "Applicant". */
  showRole?: boolean;
}

function initials(name: string): string {
  const letters = name
    .replace(/·.*$/, '')
    .split(/\s+/)
    .filter((word) => /^[\p{L}\d]/u.test(word))
    .map((word) => (/^\d+$/.test(word) ? word : word[0]));
  return (letters.length > 1 ? letters[0] + letters[1] : (letters[0] ?? '?')).toUpperCase();
}

/** Who spoke: an avatar in the speaker's colour, their name and, optionally, their role. */
export function SpeakerBadge({ speaker, size = 'md', showRole = false }: SpeakerBadgeProps) {
  const initialsText = speaker.name.startsWith('Speaker ')
    ? speaker.name.replace(/^Speaker\s+/, '').replace(/\s.*$/, '')
    : initials(speaker.name);
  return (
    <span
      className={`${styles.badge} ${styles[size]}`}
      style={{ '--speaker': `var(--series-${speaker.slot})` } as CSSProperties}
    >
      <span className={styles.avatar} aria-hidden="true">
        {initialsText}
      </span>
      <span className={styles.name}>{speaker.name}</span>
      {showRole && speaker.role ? <span className={styles.role}>{speaker.role}</span> : null}
    </span>
  );
}
